import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  Typography,
} from "@mui/material";
import { Flex } from "../../../components/Container/Flex";
import Alert from "../../../components/Alert";
import Button from "../../../components/Controls/Button";
import Switch from "../../../components/Controls/Switch";
import { IconButton } from "../../../components/IconButton";
import { Secondary, Text } from "../../../utils/style/texts";
import { Colors } from "../../../utils/style/colors";
import {
  AppIcon,
  CollapseIcon,
  DeleteIcon,
  EditIcon,
  ExpandIcon,
  PlusIcon,
  WarningIcon,
} from "../../../utils/icons";
import { AppPageAPI } from "../../../utils/API/AppPageAPI";
import AppPage, { ViewLocation } from "../../../utils/models/AppPage";
import { selectSelectedDevice } from "../../../redux/selectors/device.selector";
import { selectCurrentUser } from "../../../redux/selectors/app.selectors";
import { showAlert } from "../../AlertDialog/actions";
import injectReducer from "../../../utils/injectReducer";
import UnsyncedIndicator from "../components/UnsyncedIndicator";
import { formatSettingValue } from "../utils";
import reducer from "./reducer";
import { APP_PAGE_COMPOSER } from "./constants";
import { setEditAppPage } from "./actions";
import {
  createAppPage,
  describeAppPagesChanges,
  importLegacyAppPages,
  moveAppPageAt,
  normalizeAppPages,
  readViewLocation,
  removeAppPageAt,
  replaceAppPage,
  setAppPageEnabledAt,
  setAppPagesLocation,
  toAppPagesValue,
} from "./utils";
import Composer from "./Composer";

const LOCATION_OPTIONS = [
  { value: ViewLocation.bottomBar, label: "bottomBarAndMenu" },
  { value: ViewLocation.menu, label: "menuOnly" },
];

const LOCATION_LABELS = {
  [ViewLocation.bottomBar]: "Bottom bar and menu",
  [ViewLocation.menu]: "Menu only",
};

const locationLabel = (location, t) => {
  const option =
    LOCATION_OPTIONS.find((o) => o.value === location) ||
    LOCATION_OPTIONS.find((o) => o.value === ViewLocation.menu);
  return t(option.label, { defaultValue: LOCATION_LABELS[option.value] });
};

const isRename = (change) =>
  String(change.from ?? "") !== String(change.name ?? "");

// What happens to one view: added, deleted, edited, or renamed (its new name).
const viewChangeText = (change, t) => {
  if (change.type === "added") return t("added", { defaultValue: "Added" });
  if (change.type === "deleted")
    return t("deleted", { defaultValue: "Deleted" });
  return isRename(change)
    ? change.name
    : t("customViewEdited", { defaultValue: "Edited" });
};

// The save dialog's rows for a pending custom-views change, one per change; []
// when none explains the difference (the dialog then lists the setting itself).
export const appPagesChangeRows = (change, t) => {
  const views = t("appPages", { defaultValue: "Custom views" });
  return describeAppPagesChanges(change.from, change.to).map((c, index) => {
    const row = { id: `${change.id}#${index}`, name: change.name };
    switch (c.type) {
      case "location":
        return {
          ...row,
          label: t("customViewsLocation", {
            defaultValue: "Where custom views appear",
          }),
          from: locationLabel(c.from, t),
          to: locationLabel(c.to, t),
        };
      case "order":
        return {
          ...row,
          label: `${views}: ${t("order", { defaultValue: "Order" })}`,
          from: c.from,
          to: c.to,
        };
      case "enabled":
        return { ...row, label: `${views}: ${c.name}`, from: c.from, to: c.to };
      default:
        return {
          ...row,
          label: `${views}: ${c.name}`,
          from: c.type === "edited" && isRename(c) ? c.from : "",
          to: viewChangeText(c, t),
        };
    }
  });
};

// Same rule as toAppPagesValue: a non-empty string id no earlier row uses.
const hasOwnId = (pages, index) => {
  const id = pages[index]?.id;
  return (
    typeof id === "string" &&
    !!id &&
    pages.findIndex((p) => p.id === id) === index
  );
};

// Editor for the `general.appPages` device setting (Renderer's array arm). Each
// action sets the form value and posts the whole list as the pending (desired)
// value, like every DevCon setting; the panel's save sends it to the App.
const CustomViewsInput = ({
  id,
  name,
  value: reported,
  onChange,
  disabled,
  device,
  user,
  showAlert,
  setEditAppPage,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  // The list comes from the form; Group passes the App's reported one as `value`.
  const [{ value: raw }] = useField(id);
  const pages = React.useMemo(() => normalizeAppPages(raw), [raw]);
  const location = readViewLocation(pages);
  // Compared with the form, not the store, so a change shows before its write lands.
  const pending = React.useMemo(
    () => describeAppPagesChanges(reported, raw),
    [reported, raw]
  );
  const pendingByView = React.useMemo(() => {
    const byView = new Map();
    pending.forEach((c) => {
      if (c.id) byView.set(c.id, [...(byView.get(c.id) || []), c]);
    });
    return byView;
  }, [pending]);
  const locationChange = pending.find((c) => c.type === "location");

  const commit = React.useCallback(
    (next) => {
      const list = toAppPagesValue(next);
      setFieldValue(id, list, false);
      onChange?.({ [name]: list });
      return list;
    },
    [id, name, setFieldValue, onChange]
  );

  // The reducer is never ejected; a leftover buffer would reopen the composer.
  React.useEffect(() => () => setEditAppPage(null), [setEditAppPage]);

  // Read-only: the App falls back to these, and older builds still read them.
  const accountId =
    device?.get("v4OwnerId") ||
    device?.get("accountId") ||
    user?.accountId ||
    user?.v4ScopedAccountId;
  const isEmpty = pages.length === 0;
  const [legacy, setLegacy] = React.useState({ status: "idle", pages: [] });
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    if (!isEmpty || !accountId) return;
    let live = true;
    setLegacy({ status: "loading", pages: [] });
    new AppPageAPI().getAll([accountId]).then(
      (list) => live && setLegacy({ status: "done", pages: list || [] }),
      () => live && setLegacy({ status: "error", pages: [] })
    );
    return () => {
      live = false;
    };
  }, [isEmpty, accountId, attempt]);
  const hasLegacy = isEmpty && legacy.status === "done" && !!legacy.pages.length;
  const shownLegacyCount = legacy.pages.filter((p) => p.enabled !== false).length;

  const onCreate = () =>
    showAlert(" ", t("createAppPage", { defaultValue: "Create a custom view" }), {
      input: {
        label: t("appPageName", { defaultValue: "Name" }),
        style: { marginTop: 10 },
        fullWidth: true,
        autoFocus: true,
        disableSave: (value) => !value,
      },
      onCancel: () => {},
      onConfirm: (value) => commit(createAppPage(pages, value)),
      confirmText: t("add", { defaultValue: "Add" }),
    });

  const onDelete = (index) =>
    // showAlert is (text, title, opts) — the question is the body.
    showAlert(
      pages.length === 1
        ? t("deleteLastAppPage", {
            defaultValue:
              "Delete this custom view? With no views left, the App goes back to the views shared by its account.",
          })
        : t("deleteAppPage", { defaultValue: "Delete this custom view?" }),
      t("areYouSure?"),
      {
        onConfirm: () => commit(removeAppPageAt(pages, index)),
        confirmText: t("delete"),
        onCancel: () => {},
      }
    );

  const onEdit = (index) => {
    const list = hasOwnId(pages, index) ? pages : commit(pages);
    setEditAppPage(new AppPage(list[index]));
  };

  const onSaveComposer = (record) => {
    commit(replaceAppPage(pages, record));
    setEditAppPage(null);
  };

  const onImport = () => commit(importLegacyAppPages(legacy.pages));

  const renderViewChange = (change) => {
    if (change.type === "enabled") {
      const state = (v) => formatSettingValue(!!v, null, t);
      return `${t("enabled", { defaultValue: "Enabled" })}: ${state(
        change.from
      )} → ${state(change.to)}`;
    }
    return change.type === "edited" && isRename(change)
      ? `${t("name", { defaultValue: "Name" })}: ${change.from} → ${change.name}`
      : viewChangeText(change, t);
  };

  const renderRow = (page, index) => (
    <Box
      key={page.id || `row-${index}`}
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        px: 2,
        py: 0.75,
        borderTop: index === 0 ? "none" : `1px solid ${Colors.gray10}`,
      }}
    >
      <Switch
        size="small"
        checked={page.enabled !== false}
        disabled={disabled}
        onChange={(value) => commit(setAppPageEnabledAt(pages, index, value))}
      />
      <Box
        sx={{
          flex: "1 1 140px",
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <Text>{page.name}</Text>
        {pendingByView.has(page.id) && (
          <UnsyncedIndicator
            title={
              <Box sx={{ fontSize: "0.75rem", lineHeight: 1.5 }}>
                {pendingByView.get(page.id).map((change) => (
                  <div key={change.type}>{renderViewChange(change)}</div>
                ))}
              </Box>
            }
          />
        )}
      </Box>
      <Flex row align="center">
        <IconButton
          small
          tooltip={t("moveUp", { defaultValue: "Move up" })}
          disabled={disabled || index === 0}
          onClick={() => commit(moveAppPageAt(pages, index, -1))}
        >
          <CollapseIcon size={0.8} />
        </IconButton>
        <IconButton
          small
          tooltip={t("moveDown", { defaultValue: "Move down" })}
          disabled={disabled || index === pages.length - 1}
          onClick={() => commit(moveAppPageAt(pages, index, 1))}
        >
          <ExpandIcon size={0.8} />
        </IconButton>
        <IconButton
          small
          tooltip={t("edit")}
          disabled={disabled}
          onClick={() => onEdit(index)}
        >
          <EditIcon size={0.8} />
        </IconButton>
        <IconButton
          small
          tooltip={t("delete")}
          disabled={disabled}
          onClick={() => onDelete(index)}
        >
          <DeleteIcon size={0.8} />
        </IconButton>
      </Flex>
    </Box>
  );

  const renderEmpty = () => {
    if (legacy.status === "loading") {
      return (
        <Flex align="center" justify="center" style={{ padding: 20 }}>
          <CircularProgress size={20} />
        </Flex>
      );
    }
    if (legacy.status === "error") {
      // A failed read must not look like an account with no views.
      return (
        <Flex col style={{ padding: 12, gap: 10 }}>
          <Alert type="error" dismissable={false}>
            {t("somethingWentWrong")}
          </Alert>
          <div>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setAttempt((n) => n + 1)}
            >
              {t("retry", { defaultValue: "Retry" })}
            </Button>
          </div>
        </Flex>
      );
    }
    return (
      <Flex align="center" justify="center" style={{ padding: 20 }}>
        <Secondary>
          {hasLegacy && shownLegacyCount
            ? t("customViewsFromAccount", {
                count: shownLegacyCount,
                defaultValue:
                  "Until you add views here, this App shows the {count} custom view(s) shared by its account.",
              })
            : t("appPageIsEmpty", { defaultValue: "No custom views yet" })}
        </Secondary>
      </Flex>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
      {/* Right padding keeps clear of the Renderer's eye and unsynced marker. */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, pr: 8, pt: 1 }}
      >
        <AppIcon color="action" size={0.9} />
        <Typography variant="h6" sx={{ fontWeight: "medium" }}>
          {t(name, { defaultValue: "Custom views" })}
        </Typography>
      </Box>
      <Flex style={{ padding: "4px 16px 0" }}>
        <Secondary>
          {t("customViewsPerDevice", {
            defaultValue:
              "Custom views belong to this device. Use the save button above to send your changes to the App.",
          })}
        </Secondary>
      </Flex>
      {!!pending.length && (
        <Flex row align="center" style={{ padding: "4px 16px 0", gap: 6 }}>
          <WarningIcon size={0.6} color={Colors.orange} />
          <Secondary>
            {t("customViewsNotSent", {
              count: pending.length,
              defaultValue: "{count} change(s) not sent to the App yet",
            })}
          </Secondary>
        </Flex>
      )}
      {!!pages.length && (
        <Box
          sx={{ py: 0.75, px: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <InputLabel
            id="customViewsLocation-label"
            sx={{
              flex: 1,
              position: "relative",
              transform: "none",
              color: Colors.black,
              fontSize: "0.95rem",
            }}
          >
            {t("customViewsLocation", {
              defaultValue: "Where custom views appear",
            })}
          </InputLabel>
          {locationChange && (
            <UnsyncedIndicator
              info={{
                from: locationLabel(locationChange.from, t),
                to: locationLabel(locationChange.to, t),
              }}
            />
          )}
          <FormControl
            size="small"
            disabled={disabled}
            sx={{ width: "fit-content", maxWidth: "60%", minWidth: "160px" }}
          >
            <MuiSelect
              labelId="customViewsLocation-label"
              value={location}
              onChange={(e) => commit(setAppPagesLocation(pages, e.target.value))}
            >
              {LOCATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {locationLabel(option.value, t)}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
        </Box>
      )}
      {pages.length ? pages.map(renderRow) : renderEmpty()}
      <Flex row justify="center" style={{ padding: 12, gap: 8 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          startIcon={<PlusIcon />}
          onClick={onCreate}
        >
          {t("createAppPage", { defaultValue: "Create a custom view" })}
        </Button>
        {hasLegacy && (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={onImport}
          >
            {t("importCustomViews", { defaultValue: "Import from account" })}
          </Button>
        )}
      </Flex>
      <Composer onSave={onSaveComposer} />
    </Box>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    setEditAppPage: (page) => dispatch(setEditAppPage(page)),
    showAlert: (text, title, opts) => dispatch(showAlert(text, title, opts)),
  };
}

const mapStateToProps = createStructuredSelector({
  device: selectSelectedDevice(),
  user: selectCurrentUser(),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  // The composer buffer's only injector; without it editing silently no-ops.
  injectReducer({ key: APP_PAGE_COMPOSER, reducer })
)(CustomViewsInput);
